import { useEffect, useRef } from 'react'
import { WebSocketStatus } from '@numeo/types-core'
import { useAppSelector } from '@/hooks'

/**
 * How long the repair is spread over. A socket-service restart reconnects every operator within seconds, so
 * firing immediately turns one restart into a synchronised burst: ~130 operators, each re-running a search
 * per pinned driver plus its pooled-DAT supplement, all at once. Spread over five seconds that is tens of
 * requests a second instead of hundreds in one instant, and a lone reconnect — the common case — waits five
 * seconds at worst for loads it has already been without.
 */
const RECONNECT_JITTER_MS = 5_000

/**
 * Run a repair after the socket comes back, because nothing else does.
 *
 * A live box-truck load is fire-and-forget: socket-service drops the emit for a user whose socket is down, and
 * the reconnect replay re-applies Agentic Hub events ONLY (features/agentic-hub/store/replay-handler). So every
 * Sylectus load published while this tab was disconnected is lost for good — and with the socket manager's 10s
 * reconnect delay, even a brief blip costs several loads on a feed running ~25 a minute. That is what "the
 * board misses some loads" is, and on a longer drop, "it stopped completely".
 *
 * Each caller says what it repairs.
 *
 * Only on a RECONNECT: the first connect of a session has nothing to repair, and firing there would duplicate
 * the work the board already does on mount. `enabled` is each caller's own "there is something to repair"
 * gate, so an untouched tab never issues a request nobody asked for.
 *
 * Jittered, and deferred while the tab is hidden, because the event that makes a repair necessary is the same
 * event that makes everyone need one at once — see `RECONNECT_JITTER_MS`.
 */
export const useReconnectReconcile = (reconcile: () => void, enabled: boolean): void => {
    const status = useAppSelector((state) => state.webSocket.status)
    const hasConnectedOnce = useAppSelector((state) => state.webSocket.hasConnectedOnce)
    const missedWhileOfflineRef = useRef(false)
    // Read through a ref so a new `reconcile` identity on every render cannot re-fire the reconciliation.
    const reconcileRef = useRef(reconcile)
    reconcileRef.current = reconcile

    useEffect(() => {
        if (status !== WebSocketStatus.CONNECTED) {
            // Only a drop from an ESTABLISHED connection can have lost anything; the pre-connect state cannot.
            if (hasConnectedOnce) missedWhileOfflineRef.current = true
            return
        }
        if (!missedWhileOfflineRef.current) return
        // Consumed with nothing done, deliberately: a caller that has nothing to repair has nothing to catch
        // up on later either, and a search it runs afterwards fetches everything anyway.
        if (!enabled) {
            missedWhileOfflineRef.current = false
            return
        }

        let timer: number | undefined
        // Cleared when the repair RUNS, not when it is scheduled. The cleanup below cancels a pending timer
        // whenever a dependency changes — `enabled` flipping as the tab's search state changes, say — and
        // clearing on schedule meant that cancellation threw the repair away for good: flag already false,
        // nothing left to re-arm it, board still missing the loads. Held until it fires, a cancelled repair
        // is simply rescheduled by the effect that replaced it.
        const fire = () => {
            missedWhileOfflineRef.current = false
            reconcileRef.current()
        }
        const schedule = () => {
            document.removeEventListener('visibilitychange', onVisible)
            timer = window.setTimeout(fire, Math.random() * RECONNECT_JITTER_MS)
        }
        // A hidden tab is nobody's screen, and a restart would otherwise have every background tab an
        // operator left open join the burst. It repairs when it is looked at — which is also when the loads
        // it missed start to matter.
        const onVisible = () => {
            if (!document.hidden) schedule()
        }
        if (document.hidden) document.addEventListener('visibilitychange', onVisible)
        else schedule()
        return () => {
            document.removeEventListener('visibilitychange', onVisible)
            if (timer !== undefined) window.clearTimeout(timer)
        }
    }, [status, hasConnectedOnce, enabled])
}
